import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import defaultContent from "@/content/defaultContent";
import { getContent } from "@/lib/api";

// Deep-merge overrides onto defaults. Arrays and primitives are REPLACED,
// plain objects are merged recursively.
export function deepMerge(base, override) {
  if (Array.isArray(override)) return override;
  if (override === null || override === undefined) return base;
  if (typeof base !== "object" || Array.isArray(base) || base === null) return override;
  if (typeof override !== "object") return override;
  const out = { ...base };
  for (const k of Object.keys(override)) {
    out[k] = deepMerge(base[k], override[k]);
  }
  return out;
}

const ContentContext = createContext({
  content: defaultContent,
  overrides: {},
  loading: true,
  refresh: () => {},
});

// module-level cache so the landing renders instantly on client navigations
let _overridesCache = null;

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState(_overridesCache || {});
  const [loading, setLoading] = useState(_overridesCache === null);

  const refresh = useCallback(async () => {
    try {
      const data = await getContent();
      _overridesCache = data || {};
      setOverrides(_overridesCache);
    } catch {
      // keep defaults on failure
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const content = useMemo(() => deepMerge(defaultContent, overrides), [overrides]);

  const value = useMemo(
    () => ({ content, overrides, loading, refresh }),
    [content, overrides, loading, refresh]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

// Returns the fully-merged content object (defaults + overrides).
export function useSiteContent() {
  return useContext(ContentContext).content;
}

// Returns { content, overrides, loading, refresh } for editors.
export function useContentContext() {
  return useContext(ContentContext);
}

export default ContentContext;
